import * as React from 'react';
import { Select as SelectPrimitive } from 'radix-ui';
import { CaretDown, CaretUp, Check } from '@phosphor-icons/react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

type SelectSharedProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Root
>;

const Select = (props: SelectSharedProps) => (
  <SelectPrimitive.Root {...props} />
);

const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const selectTriggerVariants = cva(
  'flex w-full items-center justify-between gap-2 rounded-md font-sans font-normal leading-[1.5] data-[placeholder]:text-fg-secondary data-[placeholder]:opacity-75 disabled:cursor-not-allowed disabled:opacity-60 [&>span]:line-clamp-1',
  {
    variants: {
      variant: {
        default:
          'h-12 border border-border-subtle bg-surface-input px-4 text-base focus-visible:border-[var(--color-ring-default)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring-default)]',
        inline:
          'h-auto w-auto min-h-0 border border-transparent bg-transparent px-0 font-heading text-xs font-medium leading-none shadow-none focus:border-transparent focus:outline-none focus:ring-0 focus-visible:border-[var(--color-ring-default)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring-default)]',
      },
      size: {
        default: '',
        sm: 'font-heading text-xs font-medium h-10',
        large: 'text-base',
      },
      context: {
        default: '',
        modal: 'bg-[var(--color-neutral-0-10)]',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        size: 'large',
        className: 'h-14 px-5 text-base',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      context: 'default',
    },
  }
);

type SelectTriggerProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
> &
  VariantProps<typeof selectTriggerVariants>;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, variant, size, context, ...props }, ref) => {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      // Explicit tabindex: Safari excludes buttons from Tab order by default
      // but honors elements with a declared tabindex. No-op elsewhere.
      tabIndex={0}
      data-slot="select-trigger"
      data-variant={variant}
      data-size={size}
      className={cn(
        selectTriggerVariants({ variant, size, context }),
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <CaretDown
          aria-hidden="true"
          focusable="false"
          weight="fill"
          className="h-2 w-2 shrink-0"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <CaretUp
      aria-hidden="true"
      focusable="false"
      weight="fill"
      className="h-2 w-2"
    />
  </SelectPrimitive.ScrollUpButton>
));

SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <CaretDown
      aria-hidden="true"
      focusable="false"
      weight="fill"
      className="h-2 w-2"
    />
  </SelectPrimitive.ScrollDownButton>
));

SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      data-slot="select-content"
      className={cn(
        'relative z-[5000] max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-[var(--color-neutral-0-10)] bg-[var(--color-neutral-1000-70)] text-[var(--color-neutral-25)] backdrop-blur-md shadow-popover data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));

SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'px-3 py-1.5 text-sm font-medium text-[var(--color-neutral-25)]/75',
      className
    )}
    {...props}
  />
));

SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-slot="select-item"
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-3 pr-8 text-sm font-sans font-normal outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-[var(--color-neutral-0-10)] data-[highlighted]:text-[var(--color-neutral-25)]',
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className="pointer-events-none absolute right-2 flex h-4 w-4 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check aria-hidden="true" focusable="false" className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));

SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-[var(--color-neutral-0-10)]', className)}
    {...props}
  />
));

SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
